<template>
    <Card :title="$t('labels.client')">
        <div class="card-body">
            <div class="row">
                <div class="col-lg-12 col-md-4 col-sm-4">
                    <FormGroup :label="$t('labels.name')">
                        <InputGroup>
                            <template #prepend>
                                <i class="icon-user"/>
                            </template>
                            <InputAutoComplete
                                :url="`/projects/${$route.params.projectUuid}/crm/clients`"
                                :model-value="client.name"
                                :query-params="{
                                    orders: [
                                        { column: 'name', dir: 'asc'}
                                    ]
                                }"
                                @update:modelValue="client.name = $event"
                                @select="handleSelection"
                                searchOption="name"
                            >
                                <template #optionItem="{option, index}">
                                    {{ option.name }}<br/><span v-if="option.email || option.phone" class="text-muted"> {{ option.phone || option.email }}</span>
                                </template>
                            </InputAutoComplete>
                        </InputGroup>
                    </FormGroup>
                </div>
                <div class="col-lg-12 col-md-4 col-sm-4">
                    <FormGroup :label="$t('labels.phone')">
                        <InputGroup>
                            <template #prepend>
                                <i class="icon-phone2"/>
                            </template>
                            <InputAutoComplete
                                :url="`/projects/${$route.params.projectUuid}/crm/clients`"
                                :model-value="client.phone"
                                :query-params="{
                                    orders: [
                                        { column: 'phone', dir: 'asc'}
                                    ]
                                }"
                                searchOption="phone"
                                option-value="phone"
                                @update:modelValue="client.phone = $event"
                                @select="handleSelection"
                                mask="+38 (###) ###-##-##"
                                placeholder="+38 (999) 999-99-99"
                            >
                                <template #optionItem="{option, index}">
                                    {{ option.name }}<br/><span v-if="option.email || option.phone" class="text-muted"> {{ option.phone }}</span>
                                </template>
                            </InputAutoComplete>
                        </InputGroup>
                    </FormGroup>
                </div>
                <div class="col-lg-12 col-md-4 col-sm-4">
                    <FormGroup :label="$t('labels.email')">
                        <InputGroup>
                            <template #prepend>
                                <i class="icon-envelop"/>
                            </template>
                            <InputAutoComplete
                                :url="`/projects/${$route.params.projectUuid}/crm/clients`"
                                :model-value="client.email"
                                :query-params="{
                                    orders: [
                                        { column: 'email', dir: 'asc'}
                                    ]
                                }"
                                option-value="email"
                                @update:modelValue="client.email = $event"
                                @select="handleSelection"
                                searchOption="email"
                            >
                                <template #optionItem="{option, index}">
                                    {{ option.name }}<br/><span v-if="option.email || option.phone" class="text-muted"> {{ option.email }}</span>
                                </template>
                            </InputAutoComplete>
                        </InputGroup>
                    </FormGroup>
                </div>
            </div>
        </div>
        <div class="card-footer d-flex justify-content-end flex-wrap" style="gap: .5rem;">
            <template v-if="!client.uuid">
                <AppButton @click="createClient" color="indigo" :disabled="processing">
                    {{ $t('buttons.create') }}
                </AppButton>
            </template>
            <template v-else>
                <AppButton @click="deleteOrderClient" color="light" :disabled="processing">
                {{ $t('buttons.detach') }}
                </AppButton>
                <AppButton @click="updateClientInfo" color="indigo" :disabled="processing">
                    {{ $t('buttons.edit') }}
                </AppButton>
            </template>
        </div>
    </Card>
</template>
<script>
import Card from "../../../../../components/new/CardComponent.vue";
import FormGroup from "../../../../../components/new/form/group/FormGroup.vue";
import InputAutoComplete from "../../../../../components/new/form/autocomplete/InputAutoComplete.vue";
import AppButton from "../../../../../components/new/button/AppButton.vue"
import InputGroup from "../../../../../components/new/form/InputGroup.vue";

import { httpClient } from "../../../../../services/HttpClient";
import MessageNotificator from "../../../../../services/MessageNotificator";

export default {
    name: "OrderClientCard",
    components: {
        Card, FormGroup, InputAutoComplete, AppButton, InputGroup
    },
    props: {
        order: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            client: {
                name: null,
                email: null,
                phone: null,
                uuid: null
            },
            processing: false
        }
    },
    methods: {
        handleSelection(option) {
            this.client.name = option.name;
            this.client.email = option.email;
            this.client.phone = option.phone;
            this.client.uuid = option.uuid;
            this.updateOrderClient(option.uuid);
        },
        async updateOrderClient(clientUuid) {
            this.processing = true;
            await httpClient.patch(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.order.uuid}/`, {
                client_uuid: clientUuid
            }).then(() => {
                new MessageNotificator().success(this.$t('alerts.success'))
            })
            this.processing = false;
        },
        deleteOrderClient() {
            this.client = {
                name: null,
                email: null,
                phone: null,
                uuid: null
            };
            this.updateOrderClient(null);
        },
        async updateClientInfo() {
            this.processing = true;
            await httpClient.patch(`/projects/${this.$route.params.projectUuid}/crm/clients/${this.client.uuid}`, this.makeRequestBody()).then(() => {
                new MessageNotificator().success(this.$t('alerts.success'))
            })
            this.processing = false;
        },
        createClient() {
            this.processing = true;
            httpClient.post(`/projects/${this.$route.params.projectUuid}/crm/clients`, this.makeRequestBody()).then(response => {
                this.client.uuid = response.data.data.uuid;
                this.client.name = response.data.data.name;
                this.client.email = response.data.data.email;
                this.client.phone = response.data.data.phone;
                this.updateOrderClient(response.data.data.uuid);
            })
            this.processing = false;
        },
        makeRequestBody() {
            let body = {
                name: this.client.name
            };

            if (this.client.email) {
                body.email = this.client.email;
            }

            if (this.client.phone) {
                body.phone = this.client.phone;
            }

            return body;
        }
    },
    watch: {
        order: {
            immediate: true,
            handler(newValue) {
                this.client = {
                    name: newValue.client?.name || null,
                    email: newValue.client?.email || null,
                    phone: newValue.client?.phone || null,
                    uuid: newValue.client?.uuid || null
                };
            }
        }
    }
}
</script>